"use client";
import type { RowData } from "@tanstack/react-table";

import type { RecordColumnDef } from "@/components/ui/record-table/column-types";
import { EmptyValue } from "@/components/ui/record-table/empty-value";
import { AdminHoverPrefetchLink } from "@/components/navigation/admin-hover-prefetch-link";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  EmailAddress,
  type EmailAddressAttribution,
} from "@/components/email/email-address";
import { cn } from "@/lib/utils";
import type { AccessorFn, BaseColumnConfig } from "./types";
import {
  flexibleWidth,
  resolveColumnId,
  resolveAccessorProps,
  resolveString,
} from "./types";

interface LinkColumnConfig<TData extends RowData> extends BaseColumnConfig {
  accessor: keyof TData | AccessorFn<TData, string | undefined>;
  getHref: (row: TData) => string;
  primary?: boolean;
}

export function createLinkColumn<TData extends RowData>(
  config: LinkColumnConfig<TData>,
): RecordColumnDef<TData, unknown> {
  const {
    accessor,
    header,
    size,
    growMaxWidth,
    meta,
    getHref,
    primary,
    enableSorting = true,
  } = config;
  return {
    id: resolveColumnId(accessor, header),
    ...resolveAccessorProps(accessor),
    header,
    size: size ?? 200,
    meta: flexibleWidth(
      primary ? { mobilePrimary: true, ...meta } : meta,
      growMaxWidth,
    ),
    enableSorting,
    cell: ({ row }) => {
      const value = resolveString(accessor, row.original);
      if (!value) return <EmptyValue />;
      return (
        <AdminHoverPrefetchLink
          href={getHref(row.original)}
          className={
            primary
              ? "font-medium text-foreground hover:underline truncate block"
              : "text-link hover:text-link-hover hover:underline font-medium truncate block"
          }
          onClick={(e) => e.stopPropagation()}
        >
          {value}
        </AdminHoverPrefetchLink>
      );
    },
  };
}

interface PrimaryColumnConfig<TData extends RowData> extends BaseColumnConfig {
  accessor: keyof TData | AccessorFn<TData, string | undefined>;
  getHref: (row: TData) => string;
}

export function createPrimaryColumn<TData extends RowData>(
  config: PrimaryColumnConfig<TData>,
): RecordColumnDef<TData, unknown> {
  const {
    accessor,
    header,
    size,
    growMaxWidth,
    meta,
    getHref,
    enableSorting = true,
  } = config;
  return {
    id: resolveColumnId(accessor, header),
    ...resolveAccessorProps(accessor),
    header,
    size: size ?? 200,
    meta: flexibleWidth({ mobilePrimary: true, ...meta }, growMaxWidth),
    enableSorting,
    cell: ({ row }) => {
      const value = resolveString(accessor, row.original);
      if (!value) return <EmptyValue />;
      return (
        <AdminHoverPrefetchLink
          href={getHref(row.original)}
          className="font-medium text-foreground hover:underline truncate block"
          onClick={(e) => e.stopPropagation()}
        >
          {value}
        </AdminHoverPrefetchLink>
      );
    },
  };
}

interface EmailColumnConfig<TData extends RowData> extends BaseColumnConfig {
  accessor: keyof TData | AccessorFn<TData, string | undefined>;
  attribution?: (row: TData) => EmailAddressAttribution;
}

export function createEmailColumn<TData extends RowData>(
  config: EmailColumnConfig<TData>,
): RecordColumnDef<TData, unknown> {
  const {
    accessor,
    header,
    size,
    growMaxWidth,
    meta,
    attribution,
    enableSorting = true,
  } = config;
  return {
    id: resolveColumnId(accessor, header),
    ...resolveAccessorProps(accessor),
    header,
    size: size ?? 200,
    meta: flexibleWidth(meta, growMaxWidth),
    enableSorting,
    cell: ({ row }) => (
      <EmailAddress
        email={resolveString(accessor, row.original)}
        className="block text-sm"
        {...(attribution ? attribution(row.original) : {})}
      />
    ),
  };
}

interface TextColumnConfig<TData extends RowData> extends BaseColumnConfig {
  accessor: keyof TData | AccessorFn<TData, string | undefined>;
  monospace?: boolean;
  truncate?: boolean;
  muted?: boolean;
}

export function createTextColumn<TData extends RowData>(
  config: TextColumnConfig<TData>,
): RecordColumnDef<TData, unknown> {
  const {
    accessor,
    header,
    size,
    growMaxWidth,
    meta,
    monospace,
    truncate = true,
    muted,
    enableSorting = true,
  } = config;
  return {
    id: resolveColumnId(accessor, header),
    ...resolveAccessorProps(accessor),
    header,
    size: size ?? 150,
    meta: flexibleWidth(meta, growMaxWidth ?? (monospace ? 0 : undefined)),
    enableSorting,
    cell: ({ row }) => {
      const value = resolveString(accessor, row.original);
      if (!value) return <EmptyValue />;
      const tone = cn(
        "text-sm",
        monospace && "tabular-nums",
        muted && "text-muted-foreground",
      );
      return <span className={cn(tone, truncate && "truncate")}>{value}</span>;
    },
  };
}

interface IdColumnConfig<TData extends RowData> extends BaseColumnConfig {
  accessor: keyof TData | AccessorFn<TData, string | undefined>;
  truncate?: number;
}

function renderIdCell(value: string, truncate: number) {
  const displayValue =
    truncate > 0 && value.length > truncate
      ? `${value.slice(0, truncate)}…`
      : value;

  return truncate > 0 && value.length > truncate ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="tabular-nums text-xs text-muted-foreground cursor-default">
          {displayValue}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="tabular-nums text-xs">
        {value}
      </TooltipContent>
    </Tooltip>
  ) : (
    <span className="tabular-nums text-xs text-muted-foreground">{value}</span>
  );
}

export function createIdColumn<TData extends RowData>(
  config: IdColumnConfig<TData>,
): RecordColumnDef<TData, unknown> {
  const {
    accessor,
    header = "ID",
    size,
    meta,
    truncate = 8,
    enableSorting = true,
  } = config;

  return {
    id: resolveColumnId(accessor, header),
    ...resolveAccessorProps(accessor),
    header,
    size: size ?? 100,
    meta,
    enableSorting,
    cell: ({ row }) => {
      const value = resolveString(accessor, row.original);
      if (!value) return <EmptyValue />;

      return renderIdCell(value, truncate);
    },
  };
}
